/**
 * Task 3.7 — per-row time validation.
 *
 * Single source of truth for the "end > start" rule. Lives in a hook so the
 * drawer (CTA-disabled rule) and the row component (inline error copy) read
 * the same logic without duplicating dayjs imports / tz handling.
 *
 * Empty rows are valid — the locked rule from IMPLEMENTATION_PLAN.md §Task
 * 3.7 ("Empty rows are valid (translates to schedule_times: [])"): if the user
 * clears both pickers, the row carries no validation error and Task 3.8 omits
 * it from the payload's `schedule_times`.
 *
 * Implementation note: the comparison is timezone-agnostic. Both ISOs are
 * stored as absolute instants, so `dayjs(end).diff(dayjs(start))` already
 * compares correct UTC instants regardless of which tz they were entered in.
 */

import { useMemo } from "react";
import { dayjs } from "utilz";
import type { TimeSlotDraft } from "../types";

export const INVALID_TIME_RANGE_MESSAGE = "Invalid time range";
export const DUPLICATE_SCHEDULE_MESSAGE =
  "A schedule already exists for this time range";

interface TimeRowValidationResult {
  isValid: boolean;
  errorMessage: string | null;
}

/**
 * Validates a single TimeSlotDraft. Returns:
 *   - { isValid: true, errorMessage: null } when both fields are empty
 *     OR when end > start.
 *   - { isValid: false, errorMessage: "Invalid time range" } otherwise.
 */
export const validateTimeSlot = (
  slot: Pick<TimeSlotDraft, "startIso" | "endIso">,
): TimeRowValidationResult => {
  const { startIso, endIso } = slot;

  // Empty-row case (locked rule). Either both empty OR a partial fill — a
  // partial fill is also treated as "not-yet-valid-but-not-an-error": the row
  // simply doesn't contribute a payload entry. We surface no error in either
  // case to avoid noise as the user is mid-typing.
  if (!startIso && !endIso) {
    return { isValid: true, errorMessage: null };
  }
  if (!startIso || !endIso) {
    return { isValid: true, errorMessage: null };
  }

  const start = dayjs(startIso);
  const end = dayjs(endIso);

  if (!start.isValid() || !end.isValid()) {
    return { isValid: false, errorMessage: INVALID_TIME_RANGE_MESSAGE };
  }

  // end > start (zero-duration also fails per task plan: "end == start →
  // error").
  if (end.diff(start) <= 0) {
    return { isValid: false, errorMessage: INVALID_TIME_RANGE_MESSAGE };
  }

  return { isValid: true, errorMessage: null };
};

/**
 * Validates one member's slots together — single source of truth for the row
 * UI. Returns one result per slot in the same order.
 *
 * Per-slot range validity (validateTimeSlot) is always applied. Cross-slot
 * exact-duplicate detection is applied ONLY when `flagDuplicates` is true: the
 * FIRST occurrence of a given (startIso, endIso) pair stays valid; every later
 * slot with the same pair is flagged "Duplicate schedule - edit or delete".
 *
 * Duplicates are gated behind `flagDuplicates` because they are a SAVE-TIME
 * check — copying a row (which momentarily produces an identical slot the user
 * is about to edit) must not flash an error. The drawer flips this on only
 * after a Save attempt finds duplicates. Range error takes precedence over the
 * duplicate message. Empty/partial slots never count as duplicates (the key
 * requires both fields).
 */
export const validateMemberSlots = (
  timeSlots: Pick<TimeSlotDraft, "startIso" | "endIso">[],
  flagDuplicates = false,
): TimeRowValidationResult[] => {
  const seen = new Set<string>();
  return timeSlots.map((slot) => {
    const base = validateTimeSlot(slot);
    if (!base.isValid) return base;
    if (!flagDuplicates) return base;
    if (slot.startIso === null || slot.endIso === null) return base;
    const key = `${slot.startIso}|${slot.endIso}`;
    if (seen.has(key)) {
      return { isValid: false, errorMessage: DUPLICATE_SCHEDULE_MESSAGE };
    }
    seen.add(key);
    return base;
  });
};

/**
 * True iff any selected member has two slots with the identical (start, end)
 * pair. Used as the SAVE-TIME duplicate gate — not the live CTA gate (which
 * stays range-only so Save remains clickable with duplicates present).
 */
export const hasAnyDuplicateSlots = (
  selectedMap: Map<string, { timeSlots: TimeSlotDraft[] }>,
): boolean => {
  for (const entry of selectedMap.values()) {
    const results = validateMemberSlots(entry.timeSlots, true);
    if (results.some((r) => r.errorMessage === DUPLICATE_SCHEDULE_MESSAGE)) {
      return true;
    }
  }
  return false;
};

/**
 * Aggregates RANGE validity for every slot of every selected member (duplicates
 * are intentionally excluded — see hasAnyDuplicateSlots). Returns true iff at
 * least one slot has an invalid range.
 */
export const useIsAnyTimeRowInvalid = (
  selectedMap: Map<string, { timeSlots: TimeSlotDraft[] }>,
): boolean => {
  return useMemo(() => {
    for (const entry of selectedMap.values()) {
      if (validateMemberSlots(entry.timeSlots).some((r) => !r.isValid)) {
        return true;
      }
    }
    return false;
  }, [selectedMap]);
};
